/**
 * PLAT-12096 — light dismissal for the right-hand panel (AI Producer,
 * Templates, Captions, Speakers, Media, Graphics, History). They all float as
 * cards over the stage, so a press on the empty stage, an empty canvas click
 * or Escape closes whichever one is open.
 */
import { useEffect } from 'react';

/** The stage's own layout boxes: the empty workspace around the canvas. */
export const STAGE_WORKSPACE_ATTR = 'data-stage-workspace';

/**
 * Surfaces that own Escape while they are open: modals, menus, listboxes, and
 * anything with no fitting role marked `data-escape-owner` (the canvas pill).
 * Escape steps out of them first (design system: "A surface that handles
 * Escape declares it").
 */
const ESCAPE_OWNERS = [
  '[role="dialog"]',
  '[role="alertdialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  '[data-escape-owner]',
].join(',');

/**
 * Some owners stay mounted while closed: the What's New drawer is always
 * portaled in with `role="dialog"`, only `aria-hidden` and `inert`. Only an
 * owner that is actually showing holds Escape.
 */
function isShowing(owner: Element): boolean {
  if (owner.closest('[aria-hidden="true"], [inert], [hidden]')) return false;
  const style = getComputedStyle(owner);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

/**
 * Whether an Escape keydown should close the right-hand panel: not when
 * something else already handled it, not while typing in a field (Escape
 * there belongs to the field), and not while a surface that owns Escape is
 * open.
 */
export function shouldEscapeClosePanel(
  event: Pick<KeyboardEvent, 'key' | 'defaultPrevented' | 'target'>,
  doc: Pick<Document, 'querySelectorAll'> = document,
): boolean {
  if (event.key !== 'Escape' || event.defaultPrevented) return false;
  if (isEditable(event.target)) return false;
  return !Array.from(doc.querySelectorAll(ESCAPE_OWNERS)).some(isShowing);
}

/** Whether a press landed directly on the empty stage, not on a control in it. */
export function isStageWorkspacePress(target: EventTarget | null): boolean {
  return target instanceof Element && target.hasAttribute(STAGE_WORKSPACE_ATTR);
}

/** The slice of the creator store a workspace press deselects through. */
export interface WorkspaceSelection {
  activeElementIds: readonly string[];
  timelineStore: { clearActiveTimelineElements(): void };
  setActiveElements(): Promise<void>;
  deselectActiveComposition(): Promise<boolean>;
}

/**
 * A press on the empty workspace (the stage around the canvas, or the panel's
 * own column under the card): deselect, if anything is selected, and close the
 * open panel.
 */
export function dismissFromWorkspace(
  selection: WorkspaceSelection,
  closeOpenPanel: (() => void) | null,
): void {
  if (selection.activeElementIds.length > 0) {
    selection.timelineStore.clearActiveTimelineElements();
    // setActiveElements() alone re-sends the composition the renderer is in,
    // so leave it first, as a deselect on the canvas does.
    void selection.deselectActiveComposition().then(deselected => {
      if (!deselected) return selection.setActiveElements();
    });
  }
  closeOpenPanel?.();
}

/**
 * Close the open right-hand panel on Escape. `isOpen` / `close` come from the
 * host so this stays a thin listener.
 */
export function useEscapeClosesPanel(isOpen: boolean, close: () => void) {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (shouldEscapeClosePanel(event)) close();
    };
    // Capture phase: runs before a modal or menu closes itself on the same
    // Escape, so the owner check still sees it open.
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, close]);
}
